/**
 * TR-5 client API for training persistence. Every call throws on any non-2xx or
 * network error; the progress context treats a throw as "persistence unavailable"
 * and falls back to the in-memory experience (so nothing breaks before the PROD
 * migration is applied, or if the API is down).
 */
import { authenticatedFetch } from '@/lib/api';
import type { TestKey } from '@/content/training';

const BASE = '/api/v1/training';

/** A non-2xx from the training API, carrying the server's `error` code when present. */
export class TrainingApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | null,
  ) {
    super(`training api ${status}${code ? ` (${code})` : ''}`);
  }
}

async function jsonOrThrow(res: Response) {
  if (!res.ok) {
    let code: string | null = null;
    try {
      const body = await res.json();
      if (typeof body?.error === 'string') code = body.error;
    } catch {
      /* non-JSON error body */
    }
    throw new TrainingApiError(res.status, code);
  }
  return res.json();
}

const jsonPost = (path: string, body: unknown) =>
  authenticatedFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }).then(jsonOrThrow);

export interface ServerProgress {
  content_version: string;
  lessons_done?: Record<string, boolean>;
  bites_read?: Record<string, boolean>;
  cards_known?: Record<string, boolean>;
  q_stats?: Record<string, { seen: number; right: number; wrong: number }>;
  missed?: Record<string, boolean>;
  last_lesson?: { id: string; section: number } | null;
}

export interface ServerAttempt {
  id: string;
  test_key: TestKey;
  score: number;
  total: number;
  pct: number;
  passed: boolean;
  finished_at: string | null;
}

export type TrainingEvent =
  | { event_id: string; type: 'lesson_done' | 'bite_read' | 'card_known' | 'card_unknown'; id: string }
  | { event_id: string; type: 'question_right' | 'question_wrong' | 'question_seen'; id: string }
  | { event_id: string; type: 'last_lesson'; id: string; value: number };

export async function apiFetchProgress(): Promise<{ progress: ServerProgress | null }> {
  return jsonOrThrow(await authenticatedFetch(`${BASE}/progress`));
}

export async function apiFetchExamHistory(): Promise<{ attempts: ServerAttempt[] }> {
  return jsonOrThrow(await authenticatedFetch(`${BASE}/exams`));
}

export async function apiPostEvents(events: TrainingEvent[]): Promise<unknown> {
  return jsonPost(`${BASE}/progress/events`, { events });
}

export interface StartedExam {
  id: string;
  question_ids: string[];
  pass_mark: number;
  total: number;
  content_version: string;
  started_at: string;
}

export async function apiStartExam(test: TestKey): Promise<StartedExam> {
  return jsonPost(`${BASE}/exams`, { test_key: test });
}

export interface FinishedExam {
  id: string;
  test_key: TestKey;
  score: number;
  total: number;
  pct: number;
  passed: boolean;
}

export async function apiFinishExam(
  id: string,
  answers: { question_id: string; choice: number | null }[],
): Promise<FinishedExam> {
  return jsonPost(`${BASE}/exams/${id}/finish`, { answers });
}

/** A stable id for an event (retry-safe on the server ledger). */
export function newEventId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    // Older browsers without randomUUID. event_id is cast to uuid on the server
    // (training_activity.event_id is a uuid column), so the fallback MUST be a
    // valid RFC-4122 v4 UUID or every write from these browsers fails with an
    // invalid-text-representation error. Build one from getRandomValues, else
    // Math.random.
    const b = new Uint8Array(16);
    try {
      crypto.getRandomValues(b);
    } catch {
      for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
    }
    b[6] = (b[6] & 0x0f) | 0x40; // version 4
    b[8] = (b[8] & 0x3f) | 0x80; // variant 10
    const h = Array.from(b, (x) => x.toString(16).padStart(2, '0'));
    return `${h[0]}${h[1]}${h[2]}${h[3]}-${h[4]}${h[5]}-${h[6]}${h[7]}-${h[8]}${h[9]}-${h[10]}${h[11]}${h[12]}${h[13]}${h[14]}${h[15]}`;
  }
}
